import React, { useEffect, useState } from "react";

import {
    Autocomplete,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    Input,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    InputAdornment,
    Typography,
    Box,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from "@mui/material";
import Card from "react-bootstrap/Card";
import {
    PAY_METHOD_PER_UNIT,
    RULE_CRITERIA_DATE,
    RULE_CRITERIA_INT,
    RULE_CRITERIA_STRING,
} from "../../../store/constants/contract";
import {
    dateEquators,
    integerEquators,
    StringEquators,
} from "../../../store/constants/pages";

import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { FieldArray, getIn } from "formik";
import { PopoverLink } from "../../../common/popoverLink";
import AlphaSearchFields from "../../../common/dataTableHeader/AlphaSearchFields";
import { calculateMissingUnits } from "../../../util";
import InfoIcon from '@mui/icons-material/Info';
import { FeeScheduleSelection } from "../../feeSchedulePage/feeScheduleSelectionComponent";
import GlobalFeeScheduleFields from './GlobalFeeScheduleFields';
import { DateRangePicker } from "../../../common/dateRangeControl";

// Simple date formatter
const formatDate = (dateString: string | Date, format?: string): string => {
    if (!dateString) return '';
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;

    if (format === 'YYYY-MM-DD') {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // Default format: M/D/YYYY
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

const RulesSplitFieldArray = ({
    bindArrayPush,
    values,
    RULE_APPLY_AT_DATE_OF_SERVICE,
    shouldSplitPayments,
    touched,
    onClickAddSplitPaymentSection,
    setFieldValue,
    handlePayMethodChange,
    ruleCriteria,
    handleBlur,
    classes,
    errors,
    handleChange,
    payMethods,
    feeSchedules,
    feeScheduleLabels,
    getFeeScheduleLabels,
    getFeeSchedule,
    handleHoverFeeSchedule,
    popoverLeave,
    paySources,
    isPayMethodPerDiemStopLoss,
    useCustomPaymentDates
}) => {
    const handleSplitPayMethodChange = (e) => {
        const payMethodId = e.target.value;
        if (payMethodId && payMethods && payMethods.length > 0) {
            const payMethod = payMethods.find(pm => pm.id === payMethodId);
            if (payMethod && payMethod.name !== PAY_METHOD_PER_UNIT) {
                setFieldValue('useBaseRate', false);
            }
        }
        handleChange(e);
    };
    // Split Payment Fee Schedule Type: 'local' or 'global'
    // Initialize based on form value (for edit mode)
    const [splitFeeScheduleType, setSplitFeeScheduleType] = useState<'local' | 'global'>(
        values.useGlobalFeeSchedules ? 'global' : 'local'
    );
    
    // Confirmation dialog state
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingTypeChange, setPendingTypeChange] = useState<'local' | 'global' | null>(null);

    // Sync splitFeeScheduleType with values.useGlobalFeeSchedules
    // This ensures the toggle reflects the actual form value (important for edit mode)
    React.useEffect(() => {
        const expectedType = values.useGlobalFeeSchedules ? 'global' : 'local';
        if (splitFeeScheduleType !== expectedType) {
            setSplitFeeScheduleType(expectedType);
        }
    }, [values.useGlobalFeeSchedules]);

    // Handle fee schedule type change with confirmation
    const handleTypeChange = (newType: 'local' | 'global') => {
        const entries = values.ruleSplitPaymentXref || [];
        
        // Check if there are any entries with actual data (fee schedule selected)
        const hasPaymentData = entries.some((entry: any) => 
            entry.feeScheduleId || entry.globalFeeScheduleId
        );
        
        // If there are existing split payments with data, show confirmation dialog
        if (hasPaymentData) {
            setPendingTypeChange(newType);
            setShowConfirmDialog(true);
        } else {
            // No payment data, change directly and add one default entry if no entries exist
            setSplitFeeScheduleType(newType);
            setFieldValue('useGlobalFeeSchedules', newType === 'global');
            
            // If there are no entries at all, add one default empty entry
            if (entries.length === 0) {
                setFieldValue('ruleSplitPaymentXref', [{
                    equator: '',
                    value: '',
                    startsAt: null,
                    endsAt: null,
                    payMethodId: null,
                    payValue: '',
                    feeScheduleId: null,
                    feeScheduleName: '',
                    feescheduleLabelToRateXRefId: null,
                    globalFeeScheduleId: null,
                    globalFeescheduleLabelToRateXRefId: null,
                    requiresGlobalConfig: false,
                    selectedRateType: null,
                    selectedState: null,
                    selectedLocality: null,
                    selectedGpci: null,
                    globalFeeScheduleState: null,
                    globalFeeScheduleLocality: null,
                    globalFeeScheduleGPCI: null,
                    global_schedule_rate_column: null,
                    rateLabels: [],
                    feeSchedule: null
                }]);
            }
        }
    };

    // Confirm type change and add one default entry
    const confirmTypeChange = () => {
        if (pendingTypeChange) {
            setSplitFeeScheduleType(pendingTypeChange);
            setFieldValue('useGlobalFeeSchedules', pendingTypeChange === 'global');
            // Add one default empty split payment entry
            setFieldValue('ruleSplitPaymentXref', [{
                equator: '',
                value: '',
                startsAt: '',
                endsAt: '',
                payMethodId: null,
                payValue: '',
                feeScheduleId: null,
                feeScheduleName: '',
                feescheduleLabelToRateXRefId: null,
                globalFeeScheduleId: null,
                globalFeescheduleLabelToRateXRefId: null,
                requiresGlobalConfig: false,
                selectedRateType: null,
                selectedState: null,
                selectedLocality: null,
                selectedGpci: null,
                globalFeeScheduleState: null,
                globalFeeScheduleLocality: null,
                globalFeeScheduleGPCI: null,
                global_schedule_rate_column: null,
                rateLabels: [],
                feeSchedule: null
            }]);
        }
        setShowConfirmDialog(false);
        setPendingTypeChange(null);
    };

    // Cancel type change
    const cancelTypeChange = () => {
        setShowConfirmDialog(false);
        setPendingTypeChange(null);
    };
    const getHelperText = (index, name) => {
        return (
            getIn(touched, `ruleSplitPaymentXref.${index}.${name}`) &&
            getIn(errors, `ruleSplitPaymentXref.${index}.${name}`)
        );
    };

    const hasError = (index, name) => {
        return Boolean(
            getIn(touched, `ruleSplitPaymentXref.${index}.${name}`) &&
                getIn(errors, `ruleSplitPaymentXref.${index}.${name}`)
        );
    };

    // Helper function to check if pay source is Schedule
    const isPaySourceSchedule = () => {
        if (paySources && paySources.length > 0 && values.paySourceId) {
            const paySource = paySources.find(x => x.id === values.paySourceId);
            return paySource && paySource.name === "Schedule";
        }
        return false;
    };

    // Helper function to check if pay method is Anesthesia
    const isPayMethodAnesthesia = (payMethodId) => {
        if (payMethods && payMethods.length > 0) {
            const payMethod = payMethods.find(item => item.id === payMethodId);
            return payMethod && payMethod.name === 'Anesthesia';
        }
        return false;
    };

    function getEquators(fieldType) {
        switch (fieldType) {
            case RULE_CRITERIA_STRING:
                return StringEquators.equatorArr;
            case RULE_CRITERIA_DATE:
                return dateEquators.equatorArr;
            case RULE_CRITERIA_INT:
                return integerEquators.equatorArr;
            default:
                return StringEquators.equatorArr;
        }
    }

    // Auto-set toggle based on selected fee schedules in split payments
    // Only auto-set when entries actually change (fee schedules added/selected)
    useEffect(() => {
        const entries = values.ruleSplitPaymentXref || [];
        
        // Only auto-set if there are entries with fee schedules
        if (entries.length > 0 && entries.some((entry: any) => entry.feeScheduleId)) {
            // Check if any entry has a global fee schedule selected
            const hasGlobalSchedule = entries.some((entry: any) => {
                return entry.requiresGlobalConfig === true;
            });

            if (hasGlobalSchedule) {
                setSplitFeeScheduleType('global');
            } else {
                // If there are entries with fee schedules but none are global, set to local
                setSplitFeeScheduleType('local');
            }
        }
        // If no entries or no fee schedules selected, keep current toggle state
    }, [values.ruleSplitPaymentXref?.map((item: any) => item?.feeScheduleId).join(',')]);

    // Auto-fetch rate labels when component loads with existing global fee schedule (edit mode)
    useEffect(() => {
        const entries = values.ruleSplitPaymentXref || [];
        const firstEntry = entries[0];
        
        // Only fetch if:
        // 1. Using global fee schedules
        // 2. First entry has a global fee schedule ID
        // 3. Rate labels haven't been loaded yet
        // 4. getFeeScheduleLabels function is available
        if (splitFeeScheduleType === 'global' && 
            firstEntry?.globalFeeScheduleId && 
            (!firstEntry?.rateLabels || firstEntry.rateLabels.length === 0) &&
            getFeeScheduleLabels) {
            
            getFeeScheduleLabels(firstEntry.globalFeeScheduleId, (labels) => {
                // Update all split payment entries with the fetched rate labels
                entries.forEach((_, itemIndex) => {
                    setFieldValue(`ruleSplitPaymentXref.${itemIndex}.rateLabels`, labels);
                });
            }, null, setFieldValue, true); // Pass true for isGlobalSchedule
        }
    }, [splitFeeScheduleType, values.ruleSplitPaymentXref?.[0]?.globalFeeScheduleId]); // React to changes in fee schedule type and ID




    function hoverColumn(text) {
        return (
            <PopoverLink
                columnData={
                    <i className={`fas fa-question-circle text-info`}></i>
                }
                hoverData={[
                    {
                        data: text,
                    },
                ]}
            />
        );
    }

    // Validate date overlaps for split payment fee schedules
    const validateDateOverlap = (entries) => {
        if (!isPaySourceSchedule() || !entries) return null;

        const overlappingItems = new Set<number>();

        for (let i = 0; i < entries.length; i++) {
            for (let j = i + 1; j < entries.length; j++) {
                const entry1 = entries[i];
                const entry2 = entries[j];

                const effectiveDate1 = entry1?.feeSchedule?.effectiveDate;
                const expirationDate1 = entry1?.feeSchedule?.expirationDate;
                const effectiveDate2 = entry2?.feeSchedule?.effectiveDate;
                const expirationDate2 = entry2?.feeSchedule?.expirationDate;

                if (!effectiveDate1 || !expirationDate1 || !effectiveDate2 || !expirationDate2) {
                    continue;
                }

                const from1 = new Date(effectiveDate1);
                const to1 = new Date(expirationDate1);
                const from2 = new Date(effectiveDate2);
                const to2 = new Date(expirationDate2);

                // Check if different fee schedules have overlapping dates
                const isDifferentFeeSchedule = entry1?.feeScheduleId !== entry2?.feeScheduleId;
                const hasDateOverlap = from1 <= to2 && from2 <= to1;

                if (isDifferentFeeSchedule && hasDateOverlap) {
                    overlappingItems.add(i + 1);
                    overlappingItems.add(j + 1);
                }
            }
        }

        if (overlappingItems.size > 0) {
            const itemNumbers = Array.from(overlappingItems).sort((a, b) => a - b).join(', ');
            return `Split payments ${itemNumbers} have overlapping fee schedule date ranges`;
        }

        return null;
    };

    // Helper function to check if two unit ranges overlap based on equator and value(s)
    const checkUnitOverlap = (entry1, entry2) => {
        const eq1 = entry1?.equator;
        const eq2 = entry2?.equator;
        const val1 = parseFloat(entry1?.value);
        const start1 = parseFloat(entry1?.startsAt);
        const end1 = parseFloat(entry1?.endsAt);
        const val2 = parseFloat(entry2?.value);
        const start2 = parseFloat(entry2?.startsAt);
        const end2 = parseFloat(entry2?.endsAt);

        if (eq1 === 'in_between' && eq2 === 'in_between') {
            if (isNaN(start1) || isNaN(end1) || isNaN(start2) || isNaN(end2)) return false;
            return start1 <= end2 && start2 <= end1;
        }

        if (eq1 === 'equal_to' && eq2 === 'equal_to') {
            if (isNaN(val1) || isNaN(val2)) return false;
            return val1 === val2;
        }

        if (eq1 === 'equal_to' && eq2 === 'in_between') {
            if (isNaN(val1) || isNaN(start2) || isNaN(end2)) return false;
            return val1 >= start2 && val1 <= end2;
        }
        if (eq1 === 'in_between' && eq2 === 'equal_to') {
            if (isNaN(start1) || isNaN(end1) || isNaN(val2)) return false;
            return val2 >= start1 && val2 <= end1;
        }

        if ((eq1 === 'greater_than' || eq1 === 'greater_than_equal_to') &&
            (eq2 === 'greater_than' || eq2 === 'greater_than_equal_to')) {
            if (isNaN(val1) || isNaN(val2)) return false;
            return true;
        }

        if ((eq1 === 'less_than' || eq1 === 'less_than_equal_to') &&
            (eq2 === 'less_than' || eq2 === 'less_than_equal_to')) {
            if (isNaN(val1) || isNaN(val2)) return false;
            return true;
        }

        if ((eq1 === 'greater_than' || eq1 === 'greater_than_equal_to') &&
            (eq2 === 'less_than' || eq2 === 'less_than_equal_to')) {
            if (isNaN(val1) || isNaN(val2)) return false;
            const min1 = eq1 === 'greater_than' ? val1 + 1 : val1;
            const max2 = eq2 === 'less_than' ? val2 - 1 : val2;
            return min1 <= max2;
        }
        if ((eq1 === 'less_than' || eq1 === 'less_than_equal_to') &&
            (eq2 === 'greater_than' || eq2 === 'greater_than_equal_to')) {
            if (isNaN(val1) || isNaN(val2)) return false;
            const max1 = eq1 === 'less_than' ? val1 - 1 : val1;
            const min2 = eq2 === 'greater_than' ? val2 + 1 : val2;
            return min2 <= max1;
        }

        if ((eq1 === 'greater_than' || eq1 === 'greater_than_equal_to') && eq2 === 'in_between') {
            if (isNaN(val1) || isNaN(start2) || isNaN(end2)) return false;
            const min1 = eq1 === 'greater_than' ? val1 + 1 : val1;
            return min1 <= end2;
        }
        if (eq1 === 'in_between' && (eq2 === 'greater_than' || eq2 === 'greater_than_equal_to')) {
            if (isNaN(start1) || isNaN(end1) || isNaN(val2)) return false;
            const min2 = eq2 === 'greater_than' ? val2 + 1 : val2;
            return min2 <= end1;
        }

        if ((eq1 === 'less_than' || eq1 === 'less_than_equal_to') && eq2 === 'in_between') {
            if (isNaN(val1) || isNaN(start2) || isNaN(end2)) return false;
            const max1 = eq1 === 'less_than' ? val1 - 1 : val1;
            return start2 <= max1;
        }
        if (eq1 === 'in_between' && (eq2 === 'less_than' || eq2 === 'less_than_equal_to')) {
            if (isNaN(start1) || isNaN(end1) || isNaN(val2)) return false;
            const max2 = eq2 === 'less_than' ? val2 - 1 : val2;
            return start1 <= max2;
        }

        if ((eq1 === 'greater_than' || eq1 === 'greater_than_equal_to') && eq2 === 'equal_to') {
            if (isNaN(val1) || isNaN(val2)) return false;
            return eq1 === 'greater_than' ? val2 > val1 : val2 >= val1;
        }
        if (eq1 === 'equal_to' && (eq2 === 'greater_than' || eq2 === 'greater_than_equal_to')) {
            if (isNaN(val1) || isNaN(val2)) return false;
            return eq2 === 'greater_than' ? val1 > val2 : val1 >= val2;
        }

        if ((eq1 === 'less_than' || eq1 === 'less_than_equal_to') && eq2 === 'equal_to') {
            if (isNaN(val1) || isNaN(val2)) return false;
            return eq1 === 'less_than' ? val2 < val1 : val2 <= val1;
        }
        if (eq1 === 'equal_to' && (eq2 === 'less_than' || eq2 === 'less_than_equal_to')) {
            if (isNaN(val1) || isNaN(val2)) return false;
            return eq2 === 'less_than' ? val1 < val2 : val1 <= val2;
        }

        return false;
    };

    const validateUnitOverlap = (entries) => {
        if (!isPaySourceSchedule() || !entries) return null;

        const overlappingItems = new Set<number>();

        for (let i = 0; i < entries.length; i++) {
            for (let j = i + 1; j < entries.length; j++) {
                const entry1 = entries[i];
                const entry2 = entries[j];

                if(isPaySourceSchedule()) {
                    // Only check if SAME fee schedule
                    const isSameFeeSchedule = entry1?.feeScheduleId && entry2?.feeScheduleId &&
                                            entry1.feeScheduleId === entry2.feeScheduleId;

                    if (isSameFeeSchedule && checkUnitOverlap(entry1, entry2)) {
                        overlappingItems.add(i + 1);
                        overlappingItems.add(j + 1);
                    }
                }
            }
        }

        if (overlappingItems.size > 0) {
            const itemNumbers = Array.from(overlappingItems).sort((a, b) => a - b).join(', ');
            return `Split payments ${itemNumbers} have overlapping unit ranges for the same fee schedule`;
        }

        return null;
    };

    const isSplitted = values?.useSplitPayment;
    const missedUnits = calculateMissingUnits(values?.ruleSplitPaymentXref);
    const dateOverlapError = validateDateOverlap(values?.ruleSplitPaymentXref);
    const unitOverlapError = validateUnitOverlap(values?.ruleSplitPaymentXref);
    
    const entries = values.ruleSplitPaymentXref || [];

    const makeFormikPtr = (
        value: any,
        setFieldValue: (field: string, value: any) => void,
        name: string
    ) => ({
        value,
        set: (v: any) => setFieldValue(name, v),
    });
    
    return (
        <>
            {/* Confirmation Dialog */}
            <Dialog open={showConfirmDialog} onClose={cancelTypeChange}>
                <DialogTitle>Change Fee Schedule Type?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Changing the fee schedule type will clear all existing split payment entries. Do you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelTypeChange} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={confirmTypeChange} color="error" variant="contained" autoFocus>
                        Confirm & Reset
                    </Button>
                </DialogActions>
            </Dialog>

            {isSplitted && missedUnits && (
                <Typography color="error" sx={{ paddingTop: "30px", paddingLeft: "5px" }}>
                    {isSplitted && (
                        <>
                            Missing Units: <b>{missedUnits}</b> All missing
                            Units will be treated as 0
                        </>
                    )}
                </Typography>
            )}

            <FieldArray
                name="ruleSplitPaymentXref"
                render={({ remove, push }) => (
                    <div>
                        {bindArrayPush(push)}

                        {/* Show toggle above entries when split payments exist */}
                        {isSplitted && isPaySourceSchedule() && entries.length > 0 && (
                            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography
                                    sx={{
                                        margin: 0,
                                        fontFamily: 'Poppins',
                                        fontWeight: 400,
                                        fontSize: '0.875rem',
                                        lineHeight: 1.66,
                                        color: splitFeeScheduleType === 'local' ? 'primary.main' : 'text.secondary'
                                    }}
                                >
                                    Local Fee Schedules
                                </Typography>
                                <Switch
                                    checked={splitFeeScheduleType === 'global'}
                                    onChange={(e) => handleTypeChange(e.target.checked ? 'global' : 'local')}
                                    color="primary"
                                    size="small"
                                />
                                <Typography
                                    sx={{
                                        margin: 0,
                                        fontFamily: 'Poppins',
                                        fontWeight: 400,
                                        fontSize: '0.875rem',
                                        lineHeight: 1.66,
                                        color: splitFeeScheduleType === 'global' ? 'primary.main' : 'text.secondary'
                                    }}
                                >
                                    Global Fee Schedules
                                </Typography>
                            </Box>
                        )}

                        {values?.ruleSplitPaymentXref?.map((item, index) => {
                        const dateRangePtr = makeFormikPtr(
                            item.dateRange,
                            setFieldValue,
                            `ruleSplitPaymentXref.${index}.dateRange`
                        );
                        return (
                            <Card.Body key={index} style={{ padding: 0 }}>
                                {/* First row: Equator, Value, Pay Method, Pay Value, Icons */}
                                {isSplitted && (
                                    <Grid container spacing={1} alignItems="center">
                                        {/* Equator */}
                                        <Grid item xs={3}>
                                            <FormControl
                                                className={
                                                    classes.formControl
                                                }
                                            >
                                                <InputLabel
                                                    htmlFor={`ruleSplitPaymentXref.${index}.equator`}
                                                >
                                                    Equator
                                                </InputLabel>

                                                <Select
                                                    required
                                                    value={
                                                        item?.equator
                                                    }
                                                    onChange={
                                                        handleChange
                                                    }
                                                    input={
                                                        <Input
                                                            id={`ruleSplitPaymentXref.${index}.equator`}
                                                        />
                                                    }
                                                    inputProps={{
                                                        name: `ruleSplitPaymentXref.${index}.equator`,
                                                        id: `ruleSplitPaymentXref.${index}.equator`,
                                                    }}
                                                    error={Boolean(
                                                        getIn(errors, `ruleSplitPaymentXref.${index}.equator`)
                                                    )}
                                                >
                                                    {getEquators(
                                                        "int"
                                                    ).map((equator) => (
                                                        <MenuItem
                                                            key={
                                                                equator.value
                                                            }
                                                            value={
                                                                equator.value
                                                            }
                                                        >
                                                            {
                                                                equator.label
                                                            }
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText error>
                                                    {getIn(errors, `ruleSplitPaymentXref.${index}.equator`) || ' '}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>
                                        {/* Value */}
                                        <Grid item xs={2}>
                                            {item?.equator ===
                                            "in_between" ? (
                                                <FormControl className={classes.formControl}>
                                                    <AlphaSearchFields
                                                        throwInstantError={
                                                            true
                                                        }
                                                        handleBlur={
                                                            handleBlur
                                                        }
                                                        handleChange={
                                                            handleChange
                                                        }
                                                        errors={
                                                            errors
                                                                ?.ruleSplitPaymentXref
                                                                ?.length > 0
                                                                ? errors
                                                                      ?.ruleSplitPaymentXref[
                                                                      index
                                                                  ]
                                                                : null
                                                        }
                                                        touched={touched}
                                                        values={item}
                                                        startsAtName={`ruleSplitPaymentXref.${index}.startsAt`}
                                                        endsAtName={`ruleSplitPaymentXref.${index}.endsAt`}
                                                        classes={classes}

                                                        fieldType="number"
                                                    />
                                                </FormControl>
                                            ) : (
                                                <FormControl className={classes.formControl}>
                                                <TextField
                                                    required
                                                    label="Value"
                                                    name={`ruleSplitPaymentXref.${index}.value`}
                                                    className={
                                                        classes.textField
                                                    }
                                                    disabled={
                                                        item?.equator ===
                                                        ""
                                                    }
                                                    type="number"
                                                    onBlur={handleBlur}
                                                    onChange={
                                                        handleChange
                                                    }
                                                    value={item.value}
                                                    InputLabelProps={{}}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <span>
                                                                    {hoverColumn(
                                                                        "Only single numeric value is allowed"
                                                                    )}
                                                                </span>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <FormHelperText error>
                                                    {(getHelperText(index, "value") || ' ')}
                                                </FormHelperText>
                                                </FormControl>
                                            )}
                                        </Grid>

                                        {/* Pay Method */}
                                        <Grid item xs={useCustomPaymentDates ? 2 : 3}>
                                            <FormControl
                                                className={classes.formControl}
                                            >
                                                <InputLabel htmlFor="paymethodId">
                                                    Pay Method
                                                </InputLabel>
                                                <Select
                                                    required
                                                    value={
                                                        isSplitted
                                                            ? item?.payMethodId
                                                            : values.paymethodId
                                                    }
                                                    onChange={
                                                        isSplitted
                                                            ? handleSplitPayMethodChange
                                                            : handlePayMethodChange(
                                                                setFieldValue
                                                            )
                                                    }
                                                    error={
                                                        isSplitted
                                                            ? hasError(
                                                                index,
                                                                "payMethodId"
                                                            )
                                                            : Boolean(
                                                                touched.paymethodId &&
                                                                    errors.paymethodId
                                                            )
                                                    }
                                                    input={
                                                        <Input
                                                            id={
                                                                isSplitted
                                                                    ? `ruleSplitPaymentXref.${index}.payMethodId`
                                                                    : "paymethodId"
                                                            }
                                                        />
                                                    }
                                                    inputProps={{
                                                        name: isSplitted
                                                            ? `ruleSplitPaymentXref.${index}.payMethodId`
                                                            : "paymethodId",
                                                        id: isSplitted
                                                            ? `ruleSplitPaymentXref.${index}.payMethodId`
                                                            : "paymethodId",
                                                    }}
                                                >
                                                    {payMethods.map((paymethod) => (
                                                        <MenuItem
                                                            key={paymethod.id}
                                                            value={paymethod.id}
                                                        >
                                                            {paymethod.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText error sx={{whiteSpace:"pre-wrap", wordWrap: "break-word", maxWidth: "100%"}}>
                                                    {!isSplitted
                                                        ? (touched.paymethodId && errors.paymethodId ? errors.paymethodId : ' ')
                                                        : (getHelperText(index, "payMethodId") || ' ')
                                                    }
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>

                                        {useCustomPaymentDates && <Grid item xs={3}>
                                            <FormControl className={classes.formControl}>
                                                <DateRangePicker
                                                    disabled={false}
                                                    valuePtr={dateRangePtr} 
                                                    label="Date Range"
                                                    enabledSwitch={false} 
                                                    disableDateSection={false}
                                                    disableRelativeFilter={true}
                                                />
                                                <FormHelperText error sx={{whiteSpace:"pre-wrap", wordWrap: "break-word", maxWidth: "100%"}}>
                                                    {getHelperText(index, "dateRange") || ' '}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>}

                                        {/* Pay Value with Icons */}
                                        <Grid item xs={useCustomPaymentDates ? 1 : 2}>
                                            <FormControl className={classes.formControl}>
                                                <TextField
                                                    required
                                                    type="number"
                                                    label="Pay Value"
                                                    name={`ruleSplitPaymentXref.${index}.payValue`}
                                                    className={classes.textField}
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={item.payValue}
                                                    fullWidth
                                                />
                                                <FormHelperText error >
                                                    {(getHelperText(index, "payValue") || ' ')}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <Box display="flex" gap={0.5}>
                                            {values?.useSplitPayment && index == 0 && (
                                                <Tooltip title="Add Split Payment">
                                                    <IconButton
                                                        onClick={() => {
                                                            onClickAddSplitPaymentSection();
                                                        }}
                                                        size="small"
                                                    >
                                                        <AddCircleIcon
                                                            sx={{
                                                                color: "#5c79ff",
                                                            }}
                                                            fontSize="small"
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {values?.useSplitPayment &&
                                                shouldSplitPayments(values) &&
                                                values?.ruleSplitPaymentXref?.length > 1 &&
                                                index != 0 && (
                                                    <Tooltip title="Delete Split Payment">
                                                        <IconButton
                                                            onClick={() => remove(index)}
                                                            size="small"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                )}

                                {/* Second row: Fee Schedule, Rate Label, DOS Max Rate, Icons */}
                                <Grid container spacing={2} alignItems="center">
                                    {/* Pay Method - Show when NOT split payment */}
                                    {!isSplitted && (
                                        <Grid item xs={4}>
                                            <FormControl
                                                className={classes.formControl}
                                            >
                                                <InputLabel htmlFor="paymethodId">
                                                    Pay Method
                                                </InputLabel>
                                                <Select
                                                    required
                                                    value={values.paymethodId}
                                                    onChange={handlePayMethodChange(setFieldValue)}
                                                    error={Boolean(touched.paymethodId && errors.paymethodId)}
                                                    input={<Input id="paymethodId" />}
                                                    inputProps={{
                                                        name: "paymethodId",
                                                        id: "paymethodId",
                                                    }}
                                                >
                                                    {payMethods.map((paymethod) => (
                                                        <MenuItem
                                                            key={paymethod.id}
                                                            value={paymethod.id}
                                                        >
                                                            {paymethod.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                <FormHelperText error sx={{whiteSpace:"pre-wrap", wordWrap: "break-word", maxWidth: "100%"}}>
                                                    {touched.paymethodId && errors.paymethodId ? errors.paymethodId : ' '}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>
                                    )}

                                    {/* Pay Value - Show when NOT split payment */}
                                    {!isSplitted && (
                                        <Grid item xs={isPayMethodPerDiemStopLoss && isPayMethodPerDiemStopLoss(values.paymethodId) ? 2 : isPayMethodAnesthesia(values.paymethodId) ? 2 : 4}>
                                            <TextField
                                                required
                                                type="number"
                                                label="Pay Value"
                                                name="payvalue"
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.payvalue}
                                                helperText={touched.payvalue && errors.payvalue}
                                                error={Boolean(touched.payvalue && errors.payvalue)}
                                            />
                                        </Grid>
                                    )}

                                    {/* DOS Max Rate - Without split */}
                                    {!isSplitted && values.applyAt === RULE_APPLY_AT_DATE_OF_SERVICE && (
                                        <Grid item xs={2}>
                                            <TextField
                                                size="small"
                                                label="DOS Max Rate"
                                                name={`dosMaxRate`}
                                                type="number"
                                                className={classes.textField}
                                                margin="normal"
                                                value={values.dosMaxRate}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                helperText={touched.dosMaxRate && errors.dosMaxRate}
                                                error={Boolean(touched.dosMaxRate && errors.dosMaxRate)}
                                            />
                                        </Grid>
                                    )}

                                    {/* Anesthesia Toggle and Minutes Per Unit - Show when NOT split payment and Anesthesia is selected */}
                                    {!isSplitted && isPayMethodAnesthesia(values.paymethodId) && (
                                        <>
                                           
                                            <Grid item xs={3}>
                                                <Box display="flex" alignItems="center" sx={{ marginTop: '24px' }}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'Poppins',
                                                            fontWeight: 400,
                                                            fontSize: '0.875rem',
                                                            color: !values.useMinutesPerUnit ? 'primary.main' : 'text.secondary',
                                                            marginRight: 1
                                                        }}
                                                    >
                                                        Based on units
                                                    </Typography>
                                                    <Switch
                                                        checked={values.useMinutesPerUnit || false}
                                                        onChange={(e) => {
                                                            setFieldValue('useMinutesPerUnit', e.target.checked);

                                                            if (e.target.checked && !values.minutesPerUnit) {
                                                                setFieldValue('minutesPerUnit', 15);
                                                            }
                                                        }}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                   
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'Poppins',
                                                            fontWeight: 400,
                                                            fontSize: '0.875rem',
                                                            color: values.useMinutesPerUnit ? 'primary.main' : 'text.secondary',
                                                            marginLeft: 1
                                                        }}
                                                    >
                                                        Based on minutes
                                                    </Typography>
                                                </Box>
                                            </Grid>

                                             {/* Minutes Per Unit Field - Show when useMinutesPerUnit is true */}
                                            {values.useMinutesPerUnit && (
                                                <Grid item xs={2}>
                                                    <TextField
                                                        required
                                                        type="number"
                                                        label="Minutes Per Unit"
                                                        name="minutesPerUnit"
                                                        className={classes.textField}
                                                        margin="normal"
                                                        onBlur={handleBlur}
                                                        onChange={handleChange}
                                                        value={values.minutesPerUnit}
                                                        helperText={touched.minutesPerUnit && errors.minutesPerUnit}
                                                        error={Boolean(touched.minutesPerUnit && errors.minutesPerUnit)}
                                                        inputProps={{ min: 1 }}

                                                         InputProps={{
                                                                                                        endAdornment: <InputAdornment position="end">
                                                                                                             <PopoverLink
                                                        maxWidth={600}
                                                        columnData={
                                                            <i className="fas fa-question-circle text-info" style={{ cursor: 'pointer', marginLeft: 4 }}></i>
                                                        }
                                                        hoverData={[
                                                            {
                                                                label: 'Formula',
                                                                data: '(Base Units + Time Units (Charge Units /Minutes Per Units)) x Conversion Factor ( Pay Value)'
                                                            },
                                                            {
                                                                label: '',
                                                                data: '',
                                                                type: 'SPACE'
                                                            },
                                                            {
                                                                label: 'Description',
                                                                data: 'Choose whether anesthesia values come from units or minutes on the claim. If minutes are selected, enter the conversion value (default is 15). Selecting units will not change current behavior.'
                                                            }
                                                        ]}
                                                    />
                                                                                                        </InputAdornment>
                                                                                                    }}
                                                    />
                                                </Grid>
                                            )}
                                        </>
                                    )}

                                    {!isSplitted && isPayMethodPerDiemStopLoss && isPayMethodPerDiemStopLoss(values.paymethodId) && (
                                        <Grid item xs={2}>
                                            <TextField
                                                type="number"
                                                label="Pay Percentage"
                                                name="payValuePercentage"
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.payValuePercentage}
                                                helperText={touched.payValuePercentage && errors.payValuePercentage}
                                                error={Boolean(touched.payValuePercentage && errors.payValuePercentage)}
                                            />
                                        </Grid>
                                    )}

                                    {!isSplitted && isPayMethodPerDiemStopLoss && isPayMethodPerDiemStopLoss(values.paymethodId) && !values.isAlosEnabled && (
                                        <Grid item xs={2}>
                                            <TextField
                                                required
                                                type="number"
                                                label="Avg Length of Stay"
                                                name="avgLengthOfStay"
                                                className={classes.textField}
                                                margin="normal"
                                                onBlur={handleBlur}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value === '') {
                                                        handleChange(e);
                                                        return;
                                                    }
                                                    const decimalPart = value.split('.')[1];
                                                    if (!decimalPart || decimalPart.length <= 1) {
                                                        handleChange(e);
                                                    }
                                                }}
                                                value={values.avgLengthOfStay}
                                                helperText={touched.avgLengthOfStay && errors.avgLengthOfStay}
                                                error={Boolean(touched.avgLengthOfStay && errors.avgLengthOfStay)}
                                                inputProps={{ step: "0.1" }}
                                            />
                                        </Grid>
                                    )}

                                    {isSplitted && isPaySourceSchedule() && (
                                        <Grid item xs={3}>
                                            <FormControl className={classes.formControl} sx={{ width: '100%' }}>
                                                <InputLabel className={`MuiInputLabel-shrink ${classes.feeScheduleLabel}`}>
                                                    Fee Schedule
                                                </InputLabel>
                                                <Box display="flex" alignItems="center" marginTop={0} sx={{ width: '100%' }}>
                                                    <Box
                                                        onMouseEnter={(event) =>
                                                            handleHoverFeeSchedule &&
                                                            handleHoverFeeSchedule(
                                                                event.currentTarget,
                                                                item?.feeScheduleId || item?.globalFeeScheduleId,
                                                                item?.feeSchedule || item?.globalFeeSchedule,
                                                                item?.globalFeeScheduleId
                                                            )
                                                        }
                                                        onMouseLeave={popoverLeave}
                                                        onClick={popoverLeave}
                                                        sx={{
                                                            flexGrow: 1,
                                                            minWidth: 0,
                                                            maxWidth: '100%',
                                                            overflow: 'hidden',
                                                            '& .MuiChip-root': {
                                                                maxWidth: '100% !important'
                                                            },
                                                            '& .MuiChip-label': {
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                display: 'block'
                                                            }
                                                        }}
                                                    >
                                                            {/* For both global and local fee schedules, only allow first row to select fee schedule */}
                                                            {index > 0 ? (
                                                                <TextField
                                                                    fullWidth
                                                                    value={item?.feeScheduleName || ''}
                                                                    disabled
                                                                    margin="normal"
                                                                    helperText="Inherited from first row"
                                                                />
                                                            ) : (
                                                            <FeeScheduleSelection
                                                                value={
                                                                    (splitFeeScheduleType === 'global'
                                                                        ? item?.globalFeeScheduleId
                                                                        : item?.feeScheduleId
                                                                    ) ? item?.feeScheduleName : ''
                                                                }
                                                                defaultValue='Search Fee Schedule'
                                                                feeScheduleType={splitFeeScheduleType}
                                                                hideTypeToggle={true}
                                                                handleSetFeeSchedule={(rowData) => {
                                                                    // Determine if selected schedule is global (NOT based on isRBRVS!)
                                                                    // isGlobal can be boolean true or number 1
                                                                    const isGlobalSchedule = Boolean(rowData.isGlobal);
                                                                    const isRBRVSSchedule = Boolean(rowData.isRBRVS);
                                                                    const showGlobalConfig = isGlobalSchedule && isRBRVSSchedule;

                                                                    if (isGlobalSchedule) {
                                                                        setSplitFeeScheduleType('global');
                                                                        // Update form-level toggle
                                                                        setFieldValue('useGlobalFeeSchedules', true);

                                                                        // Update ALL items with the same global fee schedule and rate labels
                                                                        const allEntries = values?.ruleSplitPaymentXref || [];
                                                                        allEntries.forEach((_, itemIndex) => {
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.globalFeeScheduleId`, rowData?.id);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.feeScheduleId`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.feeScheduleName`, rowData?.name);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.feescheduleLabelToRateXRefId`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.requiresGlobalConfig`, showGlobalConfig);
                                                                        });

                                                                        // Reset global config fields only for the current item (first item typically)
                                                                        if (showGlobalConfig && index === 0) {
                                                                            setFieldValue(`ruleSplitPaymentXref.${index}.selectedRateType`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${index}.selectedState`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${index}.selectedLocality`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${index}.selectedGpci`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${index}.global_schedule_rate_column`, null);
                                                                        } else if (!showGlobalConfig && index === 0) {
                                                                            // Global non-RBRVS schedule: ensure regional fields are null for all items
                                                                            const allEntries = values?.ruleSplitPaymentXref || [];
                                                                            allEntries.forEach((_, itemIndex) => {
                                                                                setFieldValue(`ruleSplitPaymentXref.${itemIndex}.selectedRateType`, null);
                                                                                setFieldValue(`ruleSplitPaymentXref.${itemIndex}.selectedState`, null);
                                                                                setFieldValue(`ruleSplitPaymentXref.${itemIndex}.selectedLocality`, null);
                                                                                setFieldValue(`ruleSplitPaymentXref.${itemIndex}.selectedGpci`, null);
                                                                                setFieldValue(`ruleSplitPaymentXref.${itemIndex}.globalFeeScheduleState`, null);
                                                                                setFieldValue(`ruleSplitPaymentXref.${itemIndex}.globalFeeScheduleLocality`, null);
                                                                                setFieldValue(`ruleSplitPaymentXref.${itemIndex}.globalFeeScheduleGPCI`, null);
                                                                                setFieldValue(`ruleSplitPaymentXref.${itemIndex}.global_schedule_rate_column`, null);
                                                                            });
                                                                        }
                                                                    } else {
                                                                        setSplitFeeScheduleType('local');
                                                                        // Update form-level toggle
                                                                        setFieldValue('useGlobalFeeSchedules', false);

                                                                        // Update ALL items with the same local fee schedule (similar to global)
                                                                        const allEntries = values?.ruleSplitPaymentXref || [];
                                                                        allEntries.forEach((_, itemIndex) => {
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.feeScheduleId`, rowData?.id);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.globalFeeScheduleId`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.feeScheduleName`, rowData?.name);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.feescheduleLabelToRateXRefId`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.requiresGlobalConfig`, false);

                                                                            // Local fee schedule: ensure regional fields are null
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.selectedRateType`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.selectedState`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.selectedLocality`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.selectedGpci`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.globalFeeScheduleState`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.globalFeeScheduleLocality`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.globalFeeScheduleGPCI`, null);
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.global_schedule_rate_column`, null);
                                                                        });
                                                                    }

                                                                    if (getFeeSchedule && !isGlobalSchedule) {
                                                                        getFeeSchedule(rowData?.id, (feeSchedule) => {
                                                                            // Update feeSchedule for ALL items when local schedule is selected
                                                                            const allEntries = values?.ruleSplitPaymentXref || [];
                                                                            allEntries.forEach((_, itemIndex) => {
                                                                                setFieldValue(`ruleSplitPaymentXref.${itemIndex}.feeSchedule`, feeSchedule);
                                                                            });
                                                                        });
                                                                    } else if (isGlobalSchedule) {
                                                                        const globalFeeScheduleObj = {
                                                                            id: rowData?.id,
                                                                            name: rowData?.name,
                                                                            isGlobal: rowData?.isGlobal,
                                                                            isRBRVS: rowData?.isRBRVS
                                                                        };
                                                                        const allEntries = values?.ruleSplitPaymentXref || [];
                                                                        allEntries.forEach((_, itemIndex) => {
                                                                            setFieldValue(`ruleSplitPaymentXref.${itemIndex}.feeSchedule`, globalFeeScheduleObj);
                                                                        });
                                                                    }

                                                                    if (getFeeScheduleLabels) {
                                                                        getFeeScheduleLabels(rowData?.id, (labels) => {
                                                                            // Update rate labels for ALL items (both global and local)
                                                                            const allEntries = values?.ruleSplitPaymentXref || [];
                                                                            allEntries.forEach((_, itemIndex) => {
                                                                                setFieldValue(`ruleSplitPaymentXref.${itemIndex}.rateLabels`, labels || []);
                                                                            });
                                                                        }, null, setFieldValue, isGlobalSchedule);
                                                                    }
                                                                }}
                                                            />
                                                            )}
                                                    </Box>
                                                </Box>
                                                <FormHelperText error>
                                                    {getHelperText(index, 'feeScheduleId') || ' '}
                                                </FormHelperText>
                                            </FormControl>
                                        </Grid>
                                    )}

                                    {isSplitted && isPaySourceSchedule() && splitFeeScheduleType === 'local' &&
                                     item?.feeSchedule?.effectiveDate && item?.feeSchedule?.expirationDate && (
                                        <Grid item xs={3}>
                                            <Box sx={{ color: 'text.secondary', marginTop: '28px' }}>
                                                <Typography variant="body2" style={{ whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
                                                    {formatDate(item.feeSchedule.effectiveDate)} - {formatDate(item.feeSchedule.expirationDate)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    )}

                                    {/* Rate Label for LOCAL fee schedules */}
                                    {isSplitted && isPaySourceSchedule() && splitFeeScheduleType === 'local' && item?.feeScheduleId && (
                                        <Grid item xs={3}>
                                            {/* For local fee schedules, only allow first row to select rate label */}
                                            {index > 0 ? (
                                                <TextField
                                                    fullWidth
                                                    value={
                                                        item?.rateLabels?.find(
                                                            (label) => label.id === item?.feescheduleLabelToRateXRefId
                                                        )?.label || ''
                                                    }
                                                    label="Rate Label"
                                                    disabled
                                                    margin="normal"
                                                    helperText="Inherited from first row"
                                                />
                                            ) : (
                                                <Autocomplete
                                                    options={item?.rateLabels || []}
                                                    getOptionLabel={(option) => option?.label || ''}
                                                    value={
                                                        item?.rateLabels?.find(
                                                            (label) => label.id === item?.feescheduleLabelToRateXRefId
                                                        ) || null
                                                    }
                                                    onChange={(event, newValue) => {
                                                        // Update rate label for ALL items when in first row
                                                        const allEntries = values?.ruleSplitPaymentXref || [];
                                                        allEntries.forEach((_, itemIndex) => {
                                                            setFieldValue(
                                                                `ruleSplitPaymentXref.${itemIndex}.feescheduleLabelToRateXRefId`,
                                                                newValue?.id || null
                                                            );
                                                        });
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Rate Label"
                                                            margin="normal"
                                                            error={hasError(index, 'feescheduleLabelToRateXRefId')}
                                                            helperText={getHelperText(index, 'feescheduleLabelToRateXRefId')}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </Grid>
                                    )}

                                    {/* Rate Label for GLOBAL fee schedules */}
                                    {isSplitted && isPaySourceSchedule() && splitFeeScheduleType === 'global' && item?.globalFeeScheduleId && (
                                        <Grid item xs={3}>
                                            {/* For global fee schedules, only allow first row to select rate label */}
                                            {index > 0 ? (
                                                <TextField
                                                    fullWidth
                                                    value={
                                                        item?.rateLabels?.find(
                                                            (label) => label.id === item?.feescheduleLabelToRateXRefId
                                                        )?.label || ''
                                                    }
                                                    label="Rate Label"
                                                    disabled
                                                    margin="normal"
                                                    helperText="Inherited from first row"
                                                />
                                            ) : (
                                                <Autocomplete
                                                    options={item?.rateLabels || []}
                                                    getOptionLabel={(option) => option?.label || ''}
                                                    value={
                                                        item?.rateLabels?.find(
                                                            (label) => label.id === item?.feescheduleLabelToRateXRefId
                                                        ) || null
                                                    }
                                                    onChange={(event, newValue) => {
                                                        // Update rate label for ALL items when in first row
                                                        const allEntries = values?.ruleSplitPaymentXref || [];
                                                        allEntries.forEach((_, itemIndex) => {
                                                            setFieldValue(
                                                                `ruleSplitPaymentXref.${itemIndex}.feescheduleLabelToRateXRefId`,
                                                                newValue?.id || null
                                                            );
                                                        });
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Rate Label"
                                                            margin="normal"
                                                            error={hasError(index, 'feescheduleLabelToRateXRefId')}
                                                            helperText={getHelperText(index, 'feescheduleLabelToRateXRefId')}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </Grid>
                                    )}

                                </Grid>

                                {/* Global Fee Schedule Configuration - Show regional fields for global fee schedules */}
                                {/* SPLIT PAYMENT: Show regional fields ONLY for first row when using global fee schedule AND it's RBRVS */}
                                {index === 0 && splitFeeScheduleType === 'global' && item?.globalFeeScheduleId && item?.requiresGlobalConfig && (
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12}>
                                            <GlobalFeeScheduleFields
                                                scheduleId={item.globalFeeScheduleId}
                                                isRBRVS={item.requiresGlobalConfig}
                                                values={{
                                                    selectedRateType: item.selectedRateType || null,
                                                    selectedState: item.selectedState || null,
                                                    selectedLocality: item.selectedLocality || null,
                                                    selectedGpci: item.selectedGpci || null
                                                }}
                                                onChange={(field, value) => {
                                                    setFieldValue(`ruleSplitPaymentXref[${index}].${field}`, value);
                                                }}
                                                errors={{
                                                    selectedRateType: errors?.ruleSplitPaymentXref?.[index]?.selectedRateType || '',
                                                    selectedState: errors?.ruleSplitPaymentXref?.[index]?.selectedState || '',
                                                    selectedLocality: errors?.ruleSplitPaymentXref?.[index]?.selectedLocality || '',
                                                    selectedGpci: errors?.ruleSplitPaymentXref?.[index]?.selectedGpci || ''
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                            </Card.Body>
                        )
                        }
                        )}
                    </div>
                )}
            />

            {/* Date Overlap Error */}
            {dateOverlapError && (
                <FormHelperText error style={{ fontSize: '0.875rem', paddingLeft: '20px', paddingTop: '10px' }}>
                    {dateOverlapError}
                </FormHelperText>
            )}

            {/* Unit Overlap Error */}
            {unitOverlapError && (
                <FormHelperText error style={{ fontSize: '0.875rem', paddingLeft: '20px', paddingTop: '10px' }}>
                    {unitOverlapError}
                </FormHelperText>
            )}

            {isSplitted && (
                <Typography color="error" sx={{ paddingTop: "30px", paddingLeft: "5px" }}>
                    {isSplitted && (
                        <>
                            <InfoIcon /> Note: Missing Units will be treated as 0
                        </>
                    )}
                </Typography>
            )}
        </>
    );
};
export default RulesSplitFieldArray;
